import {Component, forwardRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {from, of, Subject, Subscription, timer} from 'rxjs';
import {debounce, switchMap} from 'rxjs/operators';
import {SearchService} from '../../../core/search.service';
import {SearchParameters} from '../../../core/definitions/search-parameters';
import {LoggerService} from '../../../core/logger.service';
import {QueryParserService} from '../../../core/query-parser.service';
import {ObjectTypes} from '../../../core/definitions/object-types';
import {NamedObjectType} from '../group-card/group-card.model';

interface NamedOption {
  artifact_id: string;
  name: string;
}

/**
 * Query-as-you-type Agent or NamedObject picker for the Import module's
 * "Fast verdi" (fixed value) case on a group card's named-party member
 * row. Emits the selected record's `artifact_id`; the run then passes it
 * through `value_is_resolved_id` so the backend uses the id directly
 * instead of resolving a name string (which would coin a junk record).
 *
 * Local to `admin-import-module/` on purpose - the native search-selector
 * needs a real `OperationContainer`, and there is only one caller. The
 * search is scoped to `objectType`, the sub-block's Objekttype, since the
 * backend skips a fixed pick that is not a record of that type.
 */
@Component({
  selector: 'app-named-party-select',
  templateUrl: './named-party-select.component.html',
  styleUrls: ['./named-party-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => NamedPartySelectComponent),
    },
  ],
  standalone: false,
})
export class NamedPartySelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private readonly searchService: SearchService = inject(SearchService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly queryParser: QueryParserService = inject(QueryParserService);

  @Input() label = '';
  /** The object type the search covers. A change drops the options and
   * any search in flight for the previous one. */
  @Input() set objectType(objectType: NamedObjectType) {
    if (objectType !== this._objectType) {
      this._objectType = objectType;
      this.options = [];
      this.queries.next('');
    }
  }
  get objectType(): NamedObjectType {
    return this._objectType;
  }
  private _objectType: NamedObjectType = ObjectTypes.AGENT;

  readonly inputControl = new FormControl<string | NamedOption>('', {nonNullable: true});
  options: NamedOption[] = [];
  searching = false;
  private selected: NamedOption | null = null;
  // The id of an in-flight by-id resolve; a resolve whose id no longer
  // matches this has been superseded (input cleared, retyped, another pick)
  // and must not write its stale result back.
  private resolvingId: string | null = null;

  private readonly queries = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnInit(): void {
    this.subscriptions.add(
      // An empty term (a cleared input) skips the debounce and cancels a
      // search in flight, so its late answer cannot refill the options.
      this.queries.pipe(
        debounce(term => term ? timer(250) : of(0)),
        switchMap(term => term ? from(this.search(term)) : of([] as NamedOption[])),
      ).subscribe(options => {
        this.options = options;
        this.searching = false;
      }));

    this.subscriptions.add(this.inputControl.valueChanges.subscribe(value => {
      // A cleared / retyped input drops a previous pick until a new option
      // is chosen - never leave a stale id behind free text.
      if (typeof value === 'string') {
        this.resolvingId = null;
        if (this.selected) {
          this.selected = null;
          this.onChange(null);
        }
        const term = value.trim();
        if (term.length >= 2) {
          this.searching = true;
          this.queries.next(term);
        } else {
          this.queries.next('');
        }
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.queries.complete();
  }

  displayFn = (value: string | NamedOption | null): string => {
    if (value && typeof value === 'object') {
      return value.name;
    }
    return typeof value === 'string' ? value : '';
  };

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value as NamedOption;
    this.resolvingId = null;
    this.selected = option;
    // A search still in flight must not refill the options after the pick.
    this.queries.next('');
    this.onChange(option.artifact_id);
    this.onTouched();
  }

  onBlur(): void {
    this.onTouched();
    // Free text with no matching pick is not a value.
    if (typeof this.inputControl.value === 'string' && !this.selected) {
      this.onChange(null);
    }
  }

  writeValue(value: string | null): void {
    if (!value) {
      this.resolvingId = null;
      this.selected = null;
      this.inputControl.setValue('', {emitEvent: false});
      this.queries.next('');
      return;
    }
    if (this.selected?.artifact_id === value) {
      return;
    }
    this.resolveName(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private async resolveName(artifactId: string): Promise<void> {
    this.resolvingId = artifactId;
    let option: NamedOption;
    try {
      const res = await this.searchService.search({
        fq: [`artifact_id:"${artifactId}"`],
        fl: ['artifact_name', 'artifact_id'],
        rows: 1,
      } as SearchParameters);
      const hit = res.artifacts?.[0];
      option = {
        artifact_id: artifactId,
        name: hit?.artifact_name ?? artifactId,
      };
    } catch (e) {
      this.logger.warn(`named-party select: could not resolve ${artifactId}: ${e}`);
      option = {artifact_id: artifactId, name: artifactId};
    }
    // Bail if the input moved on while we were waiting.
    if (this.resolvingId !== artifactId) {
      return;
    }
    this.resolvingId = null;
    this.selected = option;
    this.inputControl.setValue(option, {emitEvent: false});
  }

  private async search(term: string): Promise<NamedOption[]> {
    // Encoded the way the index writes *_upper fields, so spaces and
    // punctuation match their _XX escapes.
    const pattern = this.queryParser.compactUpperContainsPattern(term.replace(/\s+/g, ' '));
    if (!pattern) {
      return [];
    }
    try {
      const res = await this.searchService.search({
        fq: [`object_type:${this.objectType}`, 'valid:true', `artifact_name_upper:/${pattern}/`],
        fl: ['artifact_name', 'artifact_id'],
        rows: 20,
        sort: 'artifact_name_upper asc',
      } as SearchParameters);
      return (res.artifacts ?? []).map(art => ({
        artifact_id: art.artifact_id,
        name: art.artifact_name,
      }));
    } catch (e) {
      this.logger.error(`named-party select: search failed for "${term}": ${e}`);
      return [];
    }
  }
}
